import React, { useState, useEffect } from "react";
import Modal from "@amzn/awsui-components-react-v3/polaris/modal";
import Button from "@amzn/awsui-components-react-v3/polaris/button";
import Alert from "@amzn/awsui-components-react-v3/polaris/alert";
import { SpaceBetween } from "@amzn/awsui-components-react-v3";
import isEqual from "lodash.isequal";
import authPromiseStore from "../../http/authPromiseStore";
import LoadingState from "../../http/LoadingState";
import useBehaviorSubject from "./useBehaviorSubject";

const portComponent = window.location.port ? `:${window.location.port}` : "";

const expectedOriginUrl = `${window.location.protocol}//${window.location.hostname}${portComponent}`;
const popupUrl = `${expectedOriginUrl}/sso/refresh?og_uri=/auth-popup.html`;

/**
 * Opens popup used to refresh auth session.
 *
 * @returns {void}
 */
const popup = (): void => {
  const windowArea = {
    left: 0,
    top: 0,
    width: Math.floor(window.outerWidth * 0.8),
    height: Math.floor(window.outerHeight * 0.5),
  };

  if (windowArea.width < 1000) {
    windowArea.width = 1000;
  }
  if (windowArea.height < 630) {
    windowArea.height = 630;
  }
  windowArea.left = Math.floor(
    window.screenX + (window.outerWidth - windowArea.width) / 2
  );
  windowArea.top = Math.floor(
    window.screenY + (window.outerHeight - windowArea.height) / 8
  );

  const windowOptions = `toolbar=0,scrollbars=1,status=1,resizable=1,location=1,menuBar=0,
    width=${windowArea.width},height=${windowArea.height},
    left=${windowArea.left},top=${windowArea.top}`;

  const authWindow = window.open(popupUrl, "authPopup", windowOptions);
  const eventer = window.addEventListener;
  const messageEvent = "message";

  if (!authWindow) {
    authPromiseStore.statusSubject.next(LoadingState.REJECTED);
    return;
  }

  const timer = setInterval(() => {
    if (authWindow.closed) {
      authPromiseStore.statusSubject.next(LoadingState.REJECTED);
      clearInterval(timer);
    }
  }, 100);

  eventer(
    messageEvent,
    (event) => {
      if (event.origin === expectedOriginUrl && isEqual(event.data, {})) {
        authPromiseStore.statusSubject.next(LoadingState.RESOLVED);
      } else {
        authPromiseStore.statusSubject.next(LoadingState.REJECTED);
      }

      clearInterval(timer);
      authWindow.close();
    },
    false
  );
};

/**
 * Component that displays login refresh on 401 error.
 *
 * @returns {JSX.Element}
 */
const AuthPopup: React.FC = (): JSX.Element => {
  const [windowOpen, setWindowOpen] = useState<boolean>(false);
  const subjectStatus = useBehaviorSubject(authPromiseStore.statusSubject);

  useEffect(() => {
    if (windowOpen) {
      // Actually open popup.
      popup();
    }
  }, [windowOpen]);

  useEffect(() => {
    if (subjectStatus !== LoadingState.LOADING && windowOpen) {
      setWindowOpen(false);
    }
  }, [subjectStatus, windowOpen, setWindowOpen]);

  const onLoginClick = (): void => {
    setWindowOpen(true);
  };

  const onDismiss = (): void => {
    if (subjectStatus === LoadingState.LOADING) {
      console.log("Auth dismissed, rejecting request.");
      authPromiseStore.statusSubject.next(LoadingState.REJECTED);
    }
  };

  return (
    <Modal
      visible={subjectStatus === LoadingState.LOADING}
      header="Session Expired"
      onDismiss={onDismiss}
    >
      <SpaceBetween size="s">
        <Alert header="Your session has expired" type="error">
          Click the button below to refresh your session.
        </Alert>
        <Button variant="primary" loading={windowOpen} onClick={onLoginClick}>
          Refresh Login
        </Button>
      </SpaceBetween>
    </Modal>
  );
};

export default AuthPopup;
